import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import CommunicationService from '../../../services/communications';

const CloudCallingPanel = () => {
  const [callMessage, setCallMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [recordCall, setRecordCall] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [templatesRes, contactsRes, campaignsRes, callsRes] = await Promise.all([
        CommunicationService?.getTemplates('call'),
        CommunicationService?.getContacts(),
        CommunicationService?.getCampaigns(),
        CommunicationService?.getMessages({ type: 'call', limit: 10 })
      ]);

      if (templatesRes?.success) {
        setTemplates(templatesRes?.data || []);
      }
      if (contactsRes?.success) {
        setContacts(contactsRes?.data || []);
      }
      if (campaignsRes?.success) {
        setCampaigns(campaignsRes?.data?.filter(c => c?.type === 'call') || []);
      }
      if (callsRes?.success) {
        setCallHistory(callsRes?.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleMakeCall = async (contactId = null) => {
    const contactIds = contactId ? [contactId] : selectedContacts;
    
    if (contactIds?.length === 0) {
      setError('Please select at least one contact to call');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let successCount = 0;
      const errors = [];

      for (const cId of contactIds) {
        const contact = contacts?.find(c => c?.id === cId);
        if (!contact?.phone) {
          errors?.push(`${contact?.name || 'Unknown'}: No phone number`);
          continue;
        }

        // Create message record
        const messageRes = await CommunicationService?.createMessage({
          type: 'call',
          direction: 'outbound',
          contact_id: cId,
          template_id: selectedTemplate || null,
          content: callMessage || 'Voice call from LeadGen Pro',
          to_address: contact?.phone,
          from_address: '+15559999999', // Default caller ID
          status: 'pending'
        });

        if (messageRes?.success) {
          // Make call
          const callRes = await CommunicationService?.makeCall({
            to: contact?.phone,
            message: callMessage,
            record: recordCall,
            messageId: messageRes?.data?.id
          });

          if (callRes?.success) {
            successCount++;
            // Track usage
            await CommunicationService?.trackUsage('calls', 1);
          } else {
            errors?.push(`${contact?.name || 'Unknown'}: ${callRes?.error}`);
          }
        } else {
          errors?.push(`${contact?.name || 'Unknown'}: ${messageRes?.error}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully initiated ${successCount} call${successCount > 1 ? 's' : ''}`);
        setCallMessage('');
        setSelectedContacts([]);
        loadData(); // Refresh data
      }

      if (errors?.length > 0) {
        setError(`Errors: ${errors?.join(', ')}`);
      }
    } catch (error) {
      console.error('Error making calls:', error);
      setError('Failed to make calls');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelect = (contactId, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contactId]);
    } else {
      setSelectedContacts(selectedContacts?.filter(id => id !== contactId));
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates?.find(t => t?.id === templateId);
    if (template?.content) {
      setCallMessage(template?.content);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'failed': case'busy': case'no_answer':
        return 'text-destructive';
      case 'sent':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered':
        return 'Phone';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'PhoneOff';
      case 'busy':
        return 'PhoneMissed';
      case 'no_answer':
        return 'PhoneOff';
      case 'sent':
        return 'PhoneCall';
      default:
        return 'Phone';
    }
  };

  const templateOptions = templates?.map(template => ({
    value: template?.id,
    label: template?.name
  })) || [];

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading call data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      {/* Call Panel */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Cloud Calling</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Call Script Template"
              placeholder="Select template (optional)"
              options={templateOptions}
              value={selectedTemplate}
              onChange={handleTemplateSelect}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Call Script / Message
              </label>
              <textarea
                value={callMessage}
                onChange={(e) => setCallMessage(e?.target?.value)}
                placeholder="Enter call script or message (optional for automated calls)"
                className="w-full h-32 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={recordCall}
                onChange={(e) => setRecordCall(e?.target?.checked)}
              />
              <label className="text-sm font-medium text-foreground">
                Record call for quality assurance
              </label>
            </div>
            <Input
              label="Schedule Call (Optional)"
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e?.target?.value)}
            />
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Select Recipients</h4>
            <div className="border border-border rounded-lg max-h-64 overflow-y-auto">
              {contacts?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No contacts available. Add contacts first.
                </div>
              ) : (
                contacts?.map((contact) => (
                  <div
                    key={contact?.id}
                    className="p-3 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={selectedContacts?.includes(contact?.id)}
                        onChange={(e) => handleContactSelect(contact?.id, e?.target?.checked)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{contact?.name}</p>
                        <p className="text-xs text-muted-foreground">{contact?.phone}</p>
                        <p className="text-xs text-muted-foreground">{contact?.company}</p>
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Phone"
                          iconSize={14}
                          onClick={() => handleMakeCall(contact?.id)}
                          disabled={!contact?.phone || loading}
                        >
                          Call
                        </Button>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          contact?.is_active ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                        }`}>
                          {contact?.is_active ? 'active' : 'inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex space-x-2">
              <Button
                variant="default"
                iconName="Phone"
                iconPosition="left"
                disabled={selectedContacts?.length === 0 || loading}
                onClick={() => handleMakeCall()}
                className="flex-1"
              >
                {loading ? 'Calling...' : `Call Selected (${selectedContacts?.length})`}
              </Button>
              <Button 
                variant="outline" 
                iconName="Clock" 
                iconSize={16}
                disabled={!scheduleDate}
              >
                Schedule
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Call Campaigns */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Call Campaigns</h3>
          </div>
          <div className="p-4 space-y-4">
            {campaigns?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No call campaigns yet
              </div>
            ) : (
              campaigns?.map((campaign) => (
                <div
                  key={campaign?.id}
                  className="p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-foreground">{campaign?.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign?.status === 'sent' ?'bg-success/10 text-success' :'bg-primary/10 text-primary'
                    }`}>
                      {campaign?.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-foreground">{campaign?.sent_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Initiated</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-success">{campaign?.delivered_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Answered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-destructive">{campaign?.failed_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Missed</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>Total: {campaign?.total_contacts || 0}</span>
                    <span>Answer Rate: {campaign?.sent_count ? Math.round((campaign?.delivered_count / campaign?.sent_count) * 100) : 0}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Created: {campaign?.created_at ? new Date(campaign.created_at)?.toLocaleDateString() : 'Unknown'}
                    </span>
                    <Button variant="outline" size="sm" iconName="BarChart3" iconSize={14}>
                      Analytics
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Call History */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Recent Calls</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {callHistory?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No calls yet
              </div>
            ) : (
              callHistory?.map((call) => (
                <div
                  key={call?.id}
                  className={`p-4 border-b border-border last:border-b-0 ${
                    call?.direction === 'inbound' ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon
                      name={getStatusIcon(call?.status)}
                      size={16}
                      className={getStatusColor(call?.status)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {call?.contact?.name || 'Unknown Contact'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {call?.created_at ? new Date(call.created_at)?.toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {call?.to_address}
                      </p>
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {call?.content}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          call?.direction === 'inbound' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                        }`}>
                          {call?.direction}
                        </span>
                        <span className={`text-xs ${getStatusColor(call?.status)}`}>
                          {call?.status}
                        </span>
                        {call?.metadata?.call_duration && (
                          <span className="text-xs text-muted-foreground">
                            {call?.metadata?.call_duration}s
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudCallingPanel;